import React from 'react'
import { Table, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { IMovementRS } from '../../../../models/storageModel'
import {
  calculateMovementSummary,
  getMovementTypeLabel,
  isEntryMovement,
  isExitMovement
} from '../../../../utils/reportUtils'
import { TExcelColumn } from '../../../../utils/excelExport'
import { textNumberFormater } from '../../../../utils'
import MovementSummaryCards from './MovementSummaryCards'

const { Text } = Typography

interface MovementsByPeriodReportProps {
  data: IMovementRS[]
  storehouseMap: Map<string | number, string>
  loading?: boolean
}

export const periodExcelColumns: TExcelColumn[] = [
  { header: '#', key: 'index', width: 8, format: 'number' },
  { header: 'Fecha', key: 'date', width: 20 },
  { header: 'Nº Documento', key: 'documentNumber', width: 18 },
  { header: 'Tipo Movimiento', key: 'documentTypeLabel', width: 25 },
  { header: 'Contacto / Proveedor', key: 'contactName', width: 25 },
  { header: 'Almacén Origen', key: 'fromStorehouse', width: 22 },
  { header: 'Almacén Destino', key: 'toStorehouse', width: 22 },
  { header: 'Responsable / Creador', key: 'createdByName', width: 25 },
  { header: 'Nº Items', key: 'items', width: 10, format: 'number' },
  { header: 'Monto Total', key: 'totalAmount', width: 16, format: 'money' }
]

export function getPeriodExcelData(data: IMovementRS[], storehouseMap: Map<string | number, string>) {
  return data.map((m, i) => {
    const fromName = (m.storehouse_from_id && storehouseMap.get(m.storehouse_from_id)) || (m.storehouse_from_id ? `Almacén #${m.storehouse_from_id}` : '-')
    const toName = (m.storehouse_to_id && storehouseMap.get(m.storehouse_to_id)) || (m.storehouse_to_id ? `Almacén #${m.storehouse_to_id}` : '-')

    return {
      index: i + 1,
      date: m.created_at ? new Date(m.created_at).toLocaleString() : '',
      documentNumber: m.document_number || '-',
      documentTypeLabel: getMovementTypeLabel(m.document_type),
      contactName: m.contact_name || '-',
      fromStorehouse: fromName,
      toStorehouse: toName,
      createdByName: m.created_by_user_name || '-',
      items: m.items || m.item_list?.length || 0,
      totalAmount: m.total_amount_net || m.total_amount_full || m.total_amount || 0
    }
  })
}

export const MovementsByPeriodReport: React.FC<MovementsByPeriodReportProps> = ({
  data,
  storehouseMap,
  loading = false
}) => {
  const summary = calculateMovementSummary(data)

  const columns: ColumnsType<IMovementRS> = [
    {
      title: '#',
      key: 'index',
      width: 50,
      render: (_, __, index) => index + 1
    },
    {
      title: 'Fecha',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      sorter: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      render: (text) => (text ? new Date(text).toLocaleString() : '-')
    },
    {
      title: 'Documento',
      dataIndex: 'document_number',
      key: 'document_number',
      width: 140,
      render: (text) => <strong>{text || '-'}</strong>
    },
    {
      title: 'Tipo Movimiento',
      dataIndex: 'document_type',
      key: 'document_type',
      width: 180,
      filters: Array.from(new Set(data.map(m => m.document_type))).map(type => ({
        text: getMovementTypeLabel(type),
        value: type
      })),
      onFilter: (value, record) => record.document_type === value,
      render: (type) => {
        let color = 'default'
        if (isEntryMovement(type)) color = 'green'
        else if (isExitMovement(type)) color = 'red'
        else if (type === 'TRANSFER') color = 'blue'
        else if (type === 'ADJUSTMENT') color = 'orange'
        return <Tag color={color}>{getMovementTypeLabel(type)}</Tag>
      }
    },
    {
      title: 'Contacto',
      dataIndex: 'contact_name',
      key: 'contact_name',
      ellipsis: true,
      render: (text) => text || '-'
    },
    {
      title: 'Almacén Origen',
      dataIndex: 'storehouse_from_id',
      key: 'storehouse_from_id',
      ellipsis: true,
      render: (id, record) => (id ? storehouseMap.get(id) || storehouseMap.get(String(id)) || (record as any).storehouse_from_name || (record as any).storehouse_name || `Almacén #${id}` : '-')
    },
    {
      title: 'Almacén Destino',
      dataIndex: 'storehouse_to_id',
      key: 'storehouse_to_id',
      ellipsis: true,
      render: (id, record) => (id ? storehouseMap.get(id) || storehouseMap.get(String(id)) || (record as any).storehouse_to_name || (record as any).storehouse_name || `Almacén #${id}` : '-')
    },
    {
      title: 'Creador',
      dataIndex: 'created_by_user_name',
      key: 'created_by_user_name',
      ellipsis: true,
      render: (text) => text || '-'
    },
    {
      title: 'Items',
      key: 'items',
      width: 70,
      align: 'center',
      render: (_, record) => record.items || record.item_list?.length || 0
    },
    {
      title: 'Monto Total',
      key: 'total_amount',
      width: 130,
      align: 'right',
      sorter: (a, b) => (a.total_amount_net || a.total_amount || 0) - (b.total_amount_net || b.total_amount || 0),
      render: (_, record) => {
        const val = record.total_amount_net || record.total_amount_full || record.total_amount || 0
        return <Text strong style={{ color: '#1890ff' }}>$ {textNumberFormater(val)}</Text>
      }
    }
  ]

  return (
    <div>
      <MovementSummaryCards summary={summary} loading={loading} />

      <div style={{ overflowX: 'auto', width: '100%' }}>
        <Table<IMovementRS>
          columns={columns}
          dataSource={data.map((m, i) => ({ ...m, key: m.uid || String(i) }))}
          loading={loading}
          pagination={{
            defaultPageSize: 15,
            showSizeChanger: true,
            pageSizeOptions: ['15', '30', '50', '100'],
            showTotal: (total) => `Total ${total} movimientos`
          }}
          size="small"
          bordered
          scroll={{ x: 'max-content' }}
        />
      </div>
    </div>
  )
}

export default MovementsByPeriodReport
